import {
  AHSeparator,
  AHText,
  Box,
  ListItem,
  useSpaceSize,
  useTheme,
} from "arkh";
import React, { useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { FlatList } from "react-native";
import { OrgSearchInput } from "../OrgSearchInput";
import { distinct } from "@/src/utils";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { StoreSelector_store$key } from "./__generated__/StoreSelector_store.graphql";

type KeyedStore = StoreSelector_store$key[number];
export function StoreSelector<T extends KeyedStore>(props: {
  stores: readonly T[];
  selectedStoreIds: string[];
  disabled?: boolean;
  onStoreSetSelect: (storeIds: string[]) => void;
  renderItem: (
    store: T,
    isSelected: boolean,
    index: number
  ) => React.ReactElement | null;
}) {
  const stores = useFragment(
    graphql`
      fragment StoreSelector_store on Store @relay(plural: true) {
        id
        name
        workspaceBreadcrumbs(includeExternalId: true)
      }
    `,
    props.stores
  );

  const { onStoreSetSelect } = props;

  const selectedStoreIds = props.selectedStoreIds;

  const [search, setSearch] = useState("");
  const [order] = useState<StoreSelectorder>({
    column: "name",
    order: "ASC",
  });

  const searchQuery = search.toLowerCase();
  const filteredStores = useMemo(() => {
    return searchQuery.length > 0
      ? stores?.filter((store) => {
          return (
            store.name.toLowerCase().includes(searchQuery) ||
            store.workspaceBreadcrumbs?.some((breadcrumb) =>
              breadcrumb.toLowerCase().includes(searchQuery)
            )
          );
        })
      : stores;
  }, [searchQuery, stores]);

  const mappedStores = filteredStores?.map(
    (store) => [store, selectedStoreIds.includes(store.id)] as const
  );

  const sortedStores = mappedStores?.sort((a, b) => {
    if (order.column === "name") {
      return (
        a[0].name.localeCompare(b[0].name) * (order.order === "ASC" ? -1 : 1)
      );
    }

    if (order.column === "selected") {
      return (
        (a[1] === b[1] ? a[0].name.localeCompare(b[0].name) : a[1] ? -1 : 1) *
        (order.order === "ASC" ? -1 : 1)
      );
    }

    return 0;
  });

  const selected = useMemo(() => {
    if (selectedStoreIds.length === stores?.length) {
      return true;
    }
    if (selectedStoreIds.length === 0) {
      return false;
    }
    return "indeterminate";
  }, [selectedStoreIds, stores?.length]);

  const selectedIntent = useMemo(() => {
    return filteredStores?.every((f) => selectedStoreIds.includes(f.id));
  }, [filteredStores, selectedStoreIds]);

  const theme = useTheme();
  const paddingRight = useSpaceSize("medium");
  const intl_tokens = useIntlTokens();
  return (
    <>
      <Box color="surface-primary">
        <OrgSearchInput
          value={search}
          onChangeText={setSearch}
          mode="integrated"
        />
        <AHSeparator />

        <ListItem
          disabled={props.disabled}
          onPress={() => {
            if (selectedIntent === false) {
              onStoreSetSelect(
                distinct([
                  ...selectedStoreIds,
                  ...(filteredStores?.map((f) => f.id) ?? []),
                ])
              );
            } else {
              onStoreSetSelect(
                selectedStoreIds.filter(
                  (id) => !filteredStores?.map((f) => f.id).includes(id)
                )
              );
            }
          }}
          left={
            <ListItem.MultiSelectIndicator
              disabled={props.disabled}
              selected={selected}
            />
          }
        >
          <ListItem.TitleSecondary>
            {intl_tokens.Units}{" "}
            <AHText type="detail">
              ( {selectedStoreIds.length} / {stores?.length ?? 0} )
            </AHText>
          </ListItem.TitleSecondary>
        </ListItem>
        <AHSeparator />
      </Box>
      <FlatList
        data={sortedStores}
        style={{
          backgroundColor: theme["surface-primary"],
        }}
        contentContainerStyle={{
          paddingRight: paddingRight - 8,
          paddingBottom: paddingRight,
        }}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item: [store, selected], index }) => {
          return props.renderItem(store as unknown as T, selected, index);
        }}
      />
    </>
  );
}

type StoreReceiverSelectSort = "name" | "selected";

type StoreSelectorder = {
  column: StoreReceiverSelectSort;
  order: "ASC" | "DESC";
};
